import { Link } from "react-router-dom";
import { MEMBERSHIP, MEMBERSHIP_INTRO } from "../data/site";
import { Check } from "lucide-react";

/**
 * Elegant Concierge Plans grid — used on Home and Membership pages.
 * Silver / Gold(featured) / Platinum. Equal card heights, checkmark features,
 * gold-border hover with soft elevation.
 */
export default function ConciergePlans({ withIntro = true }) {
  return (
    <>
      {withIntro && (
        <p
          data-testid="membership-subtitle"
          className="mt-6 text-[15px] leading-relaxed text-[#0B1F3A]/70 max-w-2xl"
        >
          {MEMBERSHIP_INTRO}
        </p>
      )}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {MEMBERSHIP.map((m, i) => (
          <PlanCard key={m.tier} plan={m} index={i} />
        ))}
      </div>
    </>
  );
}

function PlanCard({ plan, index }) {
  const featured = !!plan.featured;
  const testId = `membership-card-${plan.tier.toLowerCase()}`;

  const wrapper = featured
    ? "bg-[#0B1F3A] text-white border-[#C8A96A]"
    : "bg-white text-[#0B1F3A] border-[#E6E9EF] hover:border-[#C8A96A] hover:shadow-[0_20px_50px_rgba(11,31,58,0.08)] hover:-translate-y-1";

  return (
    <div
      data-testid={testId}
      className={`group relative h-full flex flex-col p-10 border transition-[transform,box-shadow,border-color] duration-300 ${wrapper}`}
    >
      {featured && (
        <div className="absolute -top-3 left-10 bg-[#C8A96A] text-[#0B1F3A] text-[10px] tracking-[0.2em] uppercase px-3 py-1">
          Most Popular
        </div>
      )}

      <div className={`overline ${featured ? "text-[#C8A96A]" : "text-[#C8A96A]"}`}>
        0{index + 1} · {plan.category}
      </div>

      <h3
        className={`mt-4 font-serif text-5xl ${featured ? "text-white" : "text-[#0B1F3A]"}`}
      >
        {plan.tier}
      </h3>

      <p
        data-testid={`${testId}-positioning`}
        className={`mt-4 font-serif italic text-[22px] leading-snug ${
          featured ? "text-[#C8A96A]" : "text-[#0B1F3A]/90"
        }`}
      >
        {plan.positioning}
      </p>

      <p
        className={`mt-4 text-[14px] leading-relaxed ${
          featured ? "text-white/80" : "text-[#0B1F3A]/70"
        }`}
      >
        {plan.description}
      </p>

      <div
        className={`mt-8 h-px w-full ${
          featured ? "bg-white/15" : "bg-[#E6E9EF]"
        }`}
      />

      <ul className="mt-8 space-y-3.5 flex-1">
        {plan.features.map((f) => (
          <li
            key={f}
            className={`flex gap-3 text-[13.5px] leading-relaxed ${
              featured ? "text-white/85" : "text-[#0B1F3A]/80"
            }`}
          >
            <Check
              size={16}
              strokeWidth={1.75}
              className="text-[#C8A96A] shrink-0 mt-0.5"
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/contact"
        data-testid={`membership-cta-${plan.tier.toLowerCase()}`}
        className={`mt-10 inline-flex items-center justify-center h-12 px-6 rounded-full text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 ${
          featured
            ? "bg-[#C8A96A] text-[#0B1F3A] hover:bg-white"
            : "border border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white"
        }`}
      >
        {plan.cta}
      </Link>
    </div>
  );
}
